import { Injectable, inject } from '@angular/core';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { WeatherEarthquakeApi } from '../api/weather-earthquake.api';
import { convertIntervalToRange } from '../models/weather-earthquake.model';
import { WeatherEarthquakeFacade } from '../weather-earthquake.facade';
import { WeatherEarthquakeActions } from './weather-earthquake.actions';

@Injectable()
export class WeatherEarthquakeEffects {
    private actions$ = inject(Actions);
    private api = inject(WeatherEarthquakeApi);
    private licenseFacade = inject(LicenseFacade);
    private weatherEarthquakeFacade = inject(WeatherEarthquakeFacade);

    public readonly initLoadWeatherEarthquakes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WeatherEarthquakeActions.initLoadEarthquakes),
            this.licenseFacade.hasLicenseFilter(License.WeatherOverlays),
            concatMap(({ interval }) =>
                this.weatherEarthquakeFacade.canLoadData(interval).pipe(
                    take(1),
                    filter((canLoad) => canLoad),
                    map(() => WeatherEarthquakeActions.loadEarthquakes({ interval }))
                )
            )
        )
    );

    public readonly loadWeatherEarthquakes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WeatherEarthquakeActions.loadEarthquakes),
            this.licenseFacade.hasLicenseFilter(License.WeatherOverlays),
            concatMap(({ interval }) =>
                this.api.getEarthquake(convertIntervalToRange(interval)).pipe(
                    map((data) => WeatherEarthquakeActions.loadEarthquakesSuccess({ data, interval })),
                    catchError((error) => of(WeatherEarthquakeActions.loadEarthquakesFailure({ error, interval })))
                )
            )
        )
    );

    public readonly initLoadTectonicPlates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WeatherEarthquakeActions.initLoadTectonicPlates),
            this.licenseFacade.hasLicenseFilter(License.WeatherOverlays),
            withLatestFrom(this.weatherEarthquakeFacade.isLoadingTectonicPlates$),
            filter(([, isLoading]) => !isLoading),
            map(() => WeatherEarthquakeActions.loadTectonicPlates())
        )
    );

    public readonly loadTectonicPlates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WeatherEarthquakeActions.loadTectonicPlates),
            this.licenseFacade.hasLicenseFilter(License.WeatherOverlays),
            concatMap(() =>
                this.api.getTectonicPlates().pipe(
                    map((data) => WeatherEarthquakeActions.loadTectonicPlatesSuccess({ data })),
                    catchError((error) => of(WeatherEarthquakeActions.loadTectonicPlatesFailure({ error })))
                )
            )
        )
    );
}
